import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { UpsertGoalModal } from '../../components/UpsertGoalModal/UpsertGoalModal';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { FormValues, RetirementExpenses, RetirementGoalData, UpsertDTO } from './Retirement.meta';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { useRetirement } from './useRetirement';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { useUpsertGoalMutation } from '../../api/useUpsertGoalMutation';
import { ALREADY_RETIRED } from '../../Goals.meta';
import { GoalComponentProps } from '../../components/Goal/Goal.meta';
import { AssetBucketingSelect } from '../../components/AssetBucketingSelect/AssetBucketingSelect';
import { useGoalFormDisabled } from '../../hooks/useGoalFormDisabled';

const FORM_ID = 'ProfileGoals--Retirement';

interface Props extends GoalComponentProps {
  goalData: RetirementGoalData;
}

export const Retirement = ({ clientId, goalData, assetBucketingIndices, ...rest }: Props) => {
  const {
    principalName,
    spouseName,
    principalRetirementAgeOptions,
    spouseRetirementAgeOptions,
    retirementExpensesOptions,
    retirementExpenses,
    retirementExpensesAmount,
    principalRetirementAge,
    spouseRetirementAge,
    principalIsRetired,
    spouseIsRetired,
  } = useRetirement({ goalData });

  const readOnly = useGoalFormDisabled();

  const { control, watch, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      principalRetirementAge: principalIsRetired ? ALREADY_RETIRED : principalRetirementAge,
      spouseRetirementAge: spouseIsRetired ? ALREADY_RETIRED : spouseRetirementAge,
      retirementExpenses,
      retirementExpensesAmount,
      principalIsRetired,
      spouseIsRetired,
      assetBucketingIndices,
    },
    disabled: readOnly,
  });

  const watchedRetirementExpenses = watch('retirementExpenses');

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: '/Consumer/GoalUpdate/SaveRetirementGoal' });

  const onSubmit = async (values: FormValues) => {
    try {
      const upsertDTO: UpsertDTO = {
        ...values,
        principalRetirementAge: values.principalIsRetired ? null : values.principalRetirementAge,
        spouseRetirementAge: values.spouseIsRetired ? null : values.spouseRetirementAge,
      };

      await upsertGoal(upsertDTO);

      rest.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <UpsertGoalModal clientId={clientId} formId={FORM_ID} isLoading={isUpsertingGoal} {...rest}>
      {upsertGoalError && <Alert message={upsertGoalError.message} variant="error" mb="16" />}

      <Box
        id={FORM_ID}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        display="grid"
        css={`
          grid-template-columns: 1fr;
          gap: 24px;
        `}
        aria-label="Retirement goal form"
      >
        <Box
          display="grid"
          css={`
            grid-template-columns: repeat(${spouseName ? 2 : 1}, 1fr);
            gap: 16px;
          `}
        >
          <Controller
            control={control}
            name="principalRetirementAge"
            render={({ field: { value, onChange, disabled } }) => (
              <Select
                label={`${principalName}'s Retirement Age`}
                value={value ? ({ value, label: value } as any) : null}
                options={principalRetirementAgeOptions}
                onChange={opt => {
                  onChange(opt?.value);
                  setValue('principalIsRetired', opt?.value === ALREADY_RETIRED);
                }}
                isPortal
                disabled={disabled}
              />
            )}
          />

          {spouseName && (
            <Controller
              control={control}
              name="spouseRetirementAge"
              render={({ field: { value, onChange, disabled } }) => (
                <Select
                  label={`${spouseName}'s Retirement Age`}
                  value={value ? ({ value, label: value } as any) : null}
                  options={spouseRetirementAgeOptions}
                  onChange={opt => {
                    onChange(opt?.value);
                    setValue('spouseIsRetired', opt?.value === ALREADY_RETIRED);
                  }}
                  isPortal
                  disabled={disabled}
                />
              )}
            />
          )}
        </Box>

        <Box
          display="grid"
          css={`
            grid-auto-flow: column;
            gap: 16px;
          `}
        >
          <Controller
            control={control}
            name="retirementExpenses"
            render={({ field: { value, onChange, disabled } }) => (
              <Select
                label={
                  <>
                    Retirement Expenses{' '}
                    <Text as="span" color="neutral.500" fontSize="small">
                      (Today&apos;s Dollars)
                    </Text>
                  </>
                }
                tooltip="Financial experts generally suggest that 80% of your pre-retirement income will be sufficient to maintain your standard of living into retirement."
                value={retirementExpensesOptions.find(({ value: v }) => value === v)}
                options={retirementExpensesOptions}
                onChange={opt => onChange(opt?.value)}
                isPortal
                disabled={disabled}
              />
            )}
          />

          {watchedRetirementExpenses === RetirementExpenses.Other && (
            <Controller
              control={control}
              name="retirementExpensesAmount"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Annual Dollar Amount"
                  leftIcon="far fa-dollar-sign"
                  innerText="/year"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  {...field}
                />
              )}
            />
          )}
        </Box>

        <AssetBucketingSelect clientId={clientId} control={control} />
      </Box>
    </UpsertGoalModal>
  );
};
